import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Box, Drawer} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {
  APP_HEADER_HEIGHT,
  WHITE_100,
  COPILOT_DRAWER_WIDTH_MIN,
  COPILOT_DRAWER_WIDTH_MAX,
} from '../../theme';
import PdfReviewHome from './PdfReviewHome';
import PdfFootingCheck from './PdfFootingCheck';
import AgentChatComponent from '../copilot/AgentChat';
import {AgentChatProvider} from '../../context/AgentChatContext';
import PdfInconsistencyCheck from './PdfInconsistencyCheck';
import {STATUS as FOOTING_STATUS} from './hooks/usePdfFootingCheck';
import {STATUS as INCONSISTENCY_STATUS} from './hooks/usePdfInconsistencyCheck';

export const SCREENS = {
  HOME: 'home',
  FOOTING_CHECK: 'footingCheck',
  AGENT_CHAT: 'agentChat',
  INCONSISTENCY_CHECK: 'inconsistencyCheck',
};

const styles = {
  drawer: {
    marginTop: `calc(${APP_HEADER_HEIGHT} + 3rem)`,
    background: WHITE_100,
    borderLeft: '1px solid #DDE0DE',
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
    height: `calc(100vh - ${APP_HEADER_HEIGHT})`,
  },
  resizeHandle: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '6px',
    cursor: 'ew-resize',
    zIndex: 1200,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: '2px',
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
    },
    '&:hover': {
      '&::after': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
      },
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: '3px',
      height: '30px',
      backgroundColor: 'rgba(0, 0, 0, 0.15)',
      borderRadius: '1.5px',
    },
  },
};

const PdfReviewDrawer = ({
  open,
  onClose,
  footingStatus = FOOTING_STATUS.IDLE,
  footingsData = null,
  footingError = null,
  onRerunFootingsCheck,
  onFootingSelect,
  onStartFootingsCheck,
  inconsistencyStatus = INCONSISTENCY_STATUS.IDLE,
  inconsistenciesData = null,
  inconsistencyError = null,
  onRerunInconsistenciesCheck,
  onInconsistencySelect,
  onLocationSelect,
  onStartInconsistenciesCheck,
  isPdfParsed = false,
  onScreenChange,
  documentId,
  onFootingDismiss,
  onFootingDismissError,
  onInconsistencyDismiss,
  onInconsistencyDismissError,
}) => {
  const {pdfPennyChat: pdfPennyChatEnabled} = useFlags();
  const [currentScreen, setCurrentScreen] = useState(SCREENS.HOME);
  const [drawerWidth, setDrawerWidth] = useState(COPILOT_DRAWER_WIDTH_MIN);

  const drawerRef = useRef(null);
  const paperRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const drawerWidthRef = useRef(COPILOT_DRAWER_WIDTH_MIN);

  useEffect(() => {
    drawerWidthRef.current = drawerWidth;
  }, [drawerWidth]);

  // resize logic
  useEffect(() => {
    document.documentElement.classList.toggle('drawer-open', open);

    const resizeHandle = resizeHandleRef.current;
    const paperElement = paperRef.current;

    if (!resizeHandle || !open || !paperElement) {
      return;
    }

    let startX = 0;
    let startWidth = 0;
    let rafId = null;
    let isResizing = false;

    const onMouseDown = (e) => {
      e.preventDefault();
      isResizing = true;
      startX = e.clientX;
      const width =
        paperElement?.getBoundingClientRect().width || drawerWidthRef.current;
      startWidth = Math.min(width, COPILOT_DRAWER_WIDTH_MAX);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e) => {
      if (!isResizing) return;

      // Calculate new width (drawer is on right, so subtract)
      let newWidth = startWidth + (startX - e.clientX);
      newWidth = Math.max(COPILOT_DRAWER_WIDTH_MIN, newWidth);
      newWidth = Math.min(COPILOT_DRAWER_WIDTH_MAX, newWidth);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      // Use RAF for smooth animation
      rafId = requestAnimationFrame(() => {
        if (paperElement) {
          paperElement.style.width = `${newWidth}px`;
        }
      });
    };

    const onMouseUp = () => {
      isResizing = false;

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (paperElement) {
        const finalWidth =
          parseFloat(paperElement.style.width) || drawerWidthRef.current;
        drawerWidthRef.current = finalWidth;
        paperElement.style.width = ''; // Clear inline style
        setDrawerWidth(finalWidth);
      }

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    resizeHandle.addEventListener('mousedown', onMouseDown);

    return () => {
      document.documentElement.classList.remove('drawer-open');
      resizeHandle?.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [open]);

  useEffect(() => {
    onScreenChange?.(currentScreen);
  }, [currentScreen, onScreenChange]);

  // Reset screen when drawer closes
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setCurrentScreen(SCREENS.HOME);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleNavigateToFootings = useCallback(() => {
    setCurrentScreen(SCREENS.FOOTING_CHECK);
    onStartFootingsCheck?.();
  }, [onStartFootingsCheck]);

  const handleNavigateToChat = useCallback(() => {
    setCurrentScreen(SCREENS.AGENT_CHAT);
  }, []);

  const handleNavigateToInconsistencies = useCallback(() => {
    setCurrentScreen(SCREENS.INCONSISTENCY_CHECK);
    onStartInconsistenciesCheck?.();
  }, [onStartInconsistenciesCheck]);

  const handleNavigateBack = useCallback(() => {
    setCurrentScreen(SCREENS.HOME);
  }, []);

  // Navigation object for AgentChat (matches the interface it expects)
  const chatNavigation = useMemo(
    () => ({
      goBack: () => setCurrentScreen(SCREENS.HOME),
      navigate: (screen) => setCurrentScreen(screen),
    }),
    []
  );

  const renderScreen = useMemo(() => {
    switch (currentScreen) {
      case SCREENS.FOOTING_CHECK:
        return (
          <PdfFootingCheck
            status={footingStatus}
            footingsData={footingsData}
            error={footingError}
            onBack={handleNavigateBack}
            onRerunCheck={onRerunFootingsCheck}
            onFootingSelect={onFootingSelect}
            documentId={documentId}
            onFootingDismiss={onFootingDismiss}
            onFootingDismissError={onFootingDismissError}
          />
        );

      case SCREENS.AGENT_CHAT:
        if (!pdfPennyChatEnabled) {
          return (
            <PdfReviewHome
              onClose={onClose}
              onNavigateToFootings={handleNavigateToFootings}
              onNavigateToChat={handleNavigateToChat}
              onNavigateToInconsistencies={handleNavigateToInconsistencies}
              footingStatus={footingStatus}
              inconsistencyStatus={inconsistencyStatus}
              isPdfParsed={isPdfParsed}
              pdfPennyChatEnabled={pdfPennyChatEnabled}
            />
          );
        }
        return (
          <AgentChatProvider documentType="PDF">
            <AgentChatComponent open={open} navigation={chatNavigation} />
          </AgentChatProvider>
        );

      case SCREENS.INCONSISTENCY_CHECK:
        return (
          <PdfInconsistencyCheck
            status={inconsistencyStatus}
            inconsistenciesData={inconsistenciesData}
            error={inconsistencyError}
            onBack={handleNavigateBack}
            onRerunCheck={onRerunInconsistenciesCheck}
            onInconsistencySelect={onInconsistencySelect}
            onLocationSelect={onLocationSelect}
            documentId={documentId}
            onInconsistencyDismiss={onInconsistencyDismiss}
            onInconsistencyDismissError={onInconsistencyDismissError}
          />
        );

      case SCREENS.HOME:
      default:
        return (
          <PdfReviewHome
            onClose={onClose}
            onNavigateToFootings={handleNavigateToFootings}
            onNavigateToChat={handleNavigateToChat}
            onNavigateToInconsistencies={handleNavigateToInconsistencies}
            footingStatus={footingStatus}
            inconsistencyStatus={inconsistencyStatus}
            isPdfParsed={isPdfParsed}
            pdfPennyChatEnabled={pdfPennyChatEnabled}
          />
        );
    }
  }, [
    currentScreen,
    pdfPennyChatEnabled,
    footingStatus,
    footingsData,
    footingError,
    open,
    inconsistencyStatus,
    inconsistenciesData,
    inconsistencyError,
    onClose,
    handleNavigateToFootings,
    handleNavigateToChat,
    handleNavigateToInconsistencies,
    handleNavigateBack,
    chatNavigation,
    onRerunFootingsCheck,
    onFootingSelect,
    onRerunInconsistenciesCheck,
    onInconsistencySelect,
    onLocationSelect,
    isPdfParsed,
  ]);

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      onClose={onClose}
      sx={{zIndex: 998}}
      PaperProps={{
        sx: {...styles.drawer, width: drawerWidth},
        ref: paperRef,
      }}
      ref={drawerRef}
    >
      <Box ref={resizeHandleRef} sx={styles.resizeHandle} />

      {renderScreen}
    </Drawer>
  );
};

PdfReviewDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  footingStatus: PropTypes.oneOf(Object.values(FOOTING_STATUS)),
  footingsData: PropTypes.shape({
    items: PropTypes.object,
    openItems: PropTypes.object,
    numItems: PropTypes.number,
    numOpenItems: PropTypes.number,
  }),
  footingError: PropTypes.string,
  onRerunFootingsCheck: PropTypes.func,
  onFootingSelect: PropTypes.func,
  onStartFootingsCheck: PropTypes.func,
  inconsistencyStatus: PropTypes.oneOf(Object.values(INCONSISTENCY_STATUS)),
  inconsistenciesData: PropTypes.shape({
    items: PropTypes.object,
    openItems: PropTypes.object,
    numItems: PropTypes.number,
    numOpenItems: PropTypes.number,
  }),
  inconsistencyError: PropTypes.string,
  onRerunInconsistenciesCheck: PropTypes.func,
  onInconsistencySelect: PropTypes.func,
  onLocationSelect: PropTypes.func,
  onStartInconsistenciesCheck: PropTypes.func,
  isPdfParsed: PropTypes.bool,
  onScreenChange: PropTypes.func,
  documentId: PropTypes.string,
  onFootingDismiss: PropTypes.func,
  onFootingDismissError: PropTypes.func,
  onInconsistencyDismiss: PropTypes.func,
  onInconsistencyDismissError: PropTypes.func,
};

export default PdfReviewDrawer;
